/**
 * RecordResults
 *
 * Data-bound region of the Records list pane: the bulk-selection bar plus the
 * scrollable record list. Owns the useRecordsBrowser subscription, so a record
 * edit (which updates the live registry cache) re-renders only this region —
 * the filter controls live in RecordListPanel, outside the subscription, and
 * stay put.
 */
import { FC, useCallback, useRef } from 'react';

import { RecordStatus } from '#types/System.Parameters.types';
import { useRecordsBrowser } from './useRecordsBrowser';
import { resetRecordSelection, selectAllRenderedRecords, toggleRecordSelection } from './recordsBrowserState';
import RecordSelectionBar from './RecordSelectionBar';
import RecordList from './RecordList';

interface RecordResultsProps {
    entityId: string;
    recordId: string | undefined;
    query: string;
    statusFilters: RecordStatus[];
    groupIds: string[];
    onOpenRecord: (recordId: string) => void;
}

const RecordResults: FC<RecordResultsProps> = ({
    entityId,
    recordId,
    query,
    statusFilters,
    groupIds,
    onOpenRecord,
}) => {
    const { records, hasMore, loadMore, loading, refresh } = useRecordsBrowser({
        entityId,
        query,
        statusFilters,
        groupIds,
    });

    const handleAfterBulk = useCallback(() => {
        refresh();
        resetRecordSelection();
    }, [refresh]);

    /** Latest display order, read by the stable selection callbacks at click time. */
    const orderRef = useRef<string[]>([]);
    orderRef.current = records.map((r) => r.docId as string);

    const handleToggleSelection = useCallback((docId: string, shiftKey: boolean) => {
        toggleRecordSelection(docId, shiftKey, orderRef.current);
    }, []);

    const handleSelectAll = useCallback(() => {
        selectAllRenderedRecords(orderRef.current);
    }, []);

    const resetKey = `${entityId}|${query.trim().toLowerCase()}|${statusFilters.join(',')}|${groupIds.join(',')}`;
    const emptyDescription = entityId ? 'No records' : 'Select a record type to begin';

    return (
        <>
            <RecordSelectionBar
                entityId={entityId}
                statusFilters={statusFilters}
                groupIds={groupIds}
                query={query}
                onAfterBulk={handleAfterBulk}
                onSelectAll={handleSelectAll}
            />
            <RecordList
                records={records}
                activeRecordId={recordId}
                onOpen={onOpenRecord}
                onToggleSelection={handleToggleSelection}
                hasMore={hasMore}
                loadMore={loadMore}
                loading={loading}
                resetKey={resetKey}
                emptyDescription={emptyDescription}
            />
        </>
    );
};

export default RecordResults;
